import { updateOrganization, updateOrganizationInformation } from 'api'
import { Button, Modal, Table, Tooltip, Typography } from 'components'
import { MenuContext } from 'context'
import { useToast } from 'hooks'
import {
	Icons,
	OrganizationType,
	OrganizationsTableProps,
	SettingsOrganization,
	TableProps,
	TypographyTypes,
	UpdateOrganizationBodyRequest,
	UpdaterganizationRequest
} from 'interfaces'
import React, { useContext, useState } from 'react'
import { useMutation } from 'react-query'
import { useNavigate } from 'react-router'
import Cookies from 'universal-cookie'
import { defaultSortType, formatLocalTime } from 'utils'
import { OrganizationLinks } from './organizationLinks'
import { OrganizationStatusActions } from './organizationStatusActions'
import { OrganizationsTableActionsCell } from './organizationTableActionsCell'

interface ParamsPatchOrg {
	id: number
	settings: SettingsOrganization
}

type ChangeStatusPyload = {
	id: number
	body: UpdateOrganizationBodyRequest
}

const columns = (actions: {
	onEditAdminTeam: (id: string) => void
	onEditAppBuild: (id: string, users: number) => void
	onEditInformation: (id: string) => void
	onCopyAAC: (id: string) => void
	onCopyMoveOver: (id: string) => void
	onCopySSO: (id: string) => void
	onView: (id: OrganizationType) => void
	onChageStatus: (payload: ChangeStatusPyload) => void
	onEditVisibility: (params: ParamsPatchOrg) => void
}): TableProps['columns'] => [
	{
		Header: <div className="!px-3" />,
		accessor: 'id',
		disableSortBy: true,
		Cell: ({ row }) => {
			const organization = row.original as OrganizationType
			return (
				<OrganizationsTableActionsCell
					onEditAdminTeam={() => actions.onEditAdminTeam(`${organization.id}`)}
					onEditAppBuild={() =>
						actions.onEditAppBuild(`${organization.id}`, organization.users)
					}
					onEditInformation={() =>
						actions.onEditInformation(`${organization.id}`)
					}
					onCopyMoveOver={() => actions.onCopyMoveOver(`${organization.id}`)}
					onCopySSO={() => actions.onCopySSO(`${organization.id}`)}
					isVisible={organization.is_visible}
					onEditVisibility={() =>
						actions.onEditVisibility({
							id: organization.id,
							settings: {
								is_visible: !organization.is_visible,
								active_import_csv: organization.active_import_csv,
								require_otp: organization.require_otp,
								active_resource: organization.active_resource,
								active_calendar_view: Boolean(
									organization.active_calendar_view
								),
								active_my_network: organization.active_my_network
							}
						})
					}
				/>
			)
		}
	},
	{
		Header: 'Organization',
		accessor: 'name',
		sortType: defaultSortType
	},
	{
		Header: (
			<Tooltip
				text="Last Active reflects the most recent content creations, edits, and notification activity in the Admin Hub."
				useInlineBlock={false}
			>
				<div className="flex items-center gap-1 cursor-default">
					<span>Last Active</span>
					<img src={Icons.info} alt="" className="w-4 h-4 opacity-60" />
				</div>
			</Tooltip>
		),
		id: 'last_active',
		accessor: row => (row as unknown as OrganizationType).lastActive,
		disableSortBy: false,
		Cell: ({ row }) => {
			const organization = row.original as OrganizationType
			return (
				<div className="w-[85px]">
					<p className="!font-normal text-gray-9 text-justify">
						{organization.lastActive
							? formatLocalTime(organization.lastActive, 'MM/DD/YYYY hh:mm A')
							: 'N/A'}
					</p>
				</div>
			)
		}
	},
	{
		Header: 'Users',
		accessor: 'users',
		disableSortBy: false
	},
	{
		Header: 'App Build',
		accessor: 'build_app',
		disableSortBy: false
	},
	{
		Header: 'Admin Email',
		accessor: 'email',
		disableSortBy: true,
		Cell: ({ value }) => (
			<div
				className="break-words whitespace-normal overflow-hidden max-w-[160px] 1lg:max-w-[270px]"
				title={value}
			>
				{value}
			</div>
		)
	},
	{
		Header: 'Address',
		accessor: 'address',
		disableSortBy: true
	},
	{
		Header: 'Main Phone',
		accessor: 'phoneNumber',
		disableSortBy: true
	},
	{
		Header: 'Status ORG',
		accessor: 'organization_type',
		disableSortBy: false,
		Cell: ({ value, row }) => {
			const organization = row.original as OrganizationType
			return (
				<OrganizationStatusActions
					orgStatus={value}
					onChageStatus={type =>
						actions.onChageStatus({
							id: organization.id,
							body: {
								organization: {
									organization_type: type
								}
							}
						})
					}
				/>
			)
		}
	},
	{
		Header: '',
		accessor: 'shareUrl',
		disableSortBy: true,
		Cell: ({ row }) => {
			const organization = row.original as OrganizationType
			return (
				<OrganizationLinks
					organization={organization}
					onCopyAAC={() => actions.onCopyAAC(organization.shareUrl ?? '')}
					onCopySSO={() => actions.onCopySSO(organization.branchUrlSso ?? '')}
					onCopyMoveOver={() =>
						actions.onCopyMoveOver(organization.branchUrlMigration ?? '')
					}
				/>
			)
		}
	},
	{
		Header: '',
		disableSortBy: true,
		accessor: 'view',
		Cell: ({ row }) => (
			<Button
				label="View"
				className="!w-max !h-min !min-w-[unset] !px-4 !py-1"
				color="Transparent"
				onClick={() => {
					const organization = row.original as OrganizationType
					actions.onView(organization)
				}}
			/>
		)
	}
]

export const OrganizationsTable: React.FC<OrganizationsTableProps> = ({
	organizations,
	refetchOrganizations,
	setSort,
	...tableProps
}) => {
	const navigate = useNavigate()
	const { showToast } = useToast()
	const { setOrganizationLogo } = useContext(MenuContext)
	const [open, setOpen] = useState(false)
	const [cantUsers, setCantUsers] = useState(0)

	const handleOptionsEdit = (id: string, acction: string): void => {
		navigate('/superAdmin/organization/create', {
			state: {
				editing: true,
				flag: acction,
				id
			}
		})
	}

	const { mutate: onChageStatus, isLoading } = useMutation({
		mutationFn: (params: ChangeStatusPyload) =>
			updateOrganization(params.id, params.body),
		onSuccess: response => {
			refetchOrganizations?.()
			showToast(
				'Success',
				response?.message ?? 'Organization updated successfully',
				'success'
			)
		}
	})

	const handleOptionsBuildApp = (id: string, users: number): void => {
		if (users > 0) {
			setCantUsers(users)
			setOpen(true)
		} else {
			handleOptionsEdit(id, 'buildApp')
		}
	}

	const handleOptionsAdminTeam = (id: string): void => {
		handleOptionsEdit(id, 'admin')
	}
	const handleOptionsInformation = (id: string): void => {
		handleOptionsEdit(id, 'organization')
	}

	const handleAACCopy = (aac: string): void => {
		navigator.clipboard.writeText(aac)
		showToast('Copied', 'AAC copied to the clipboard', 'success')
	}

	const handleCopyMoveOver = (moveOver: string): void => {
		navigator.clipboard.writeText(moveOver)
		showToast('Copied', 'Move Over copied to the clipboard', 'success')
	}
	const handleCopySSO = (sso: string): void => {
		navigator.clipboard.writeText(sso)
		showToast('Copied', 'SSO copied to the clipboard', 'success')
	}

	const handleView = (organization: OrganizationType): void => {
		const cookies = new Cookies()
		cookies.remove('organization_id')
		cookies.remove('organization_name')
		cookies.remove('organization_slug')
		cookies.remove('organization_logo', { path: '/' })
		cookies.set('organization_id', organization.id, {
			path: '/'
		})
		cookies.set('organization_name', organization.name, {
			path: '/'
		})
		cookies.set('organization_slug', organization.slug, {
			path: '/'
		})
		cookies.set('organization_logo', organization.logo, {
			path: '/'
		})
		// Update context with new organization logo
		setOrganizationLogo(organization.logo || '')
		navigate('/')
	}

	const { mutate } = useMutation({
		mutationFn: (params: ParamsPatchOrg) => {
			const dataOrg: Partial<UpdaterganizationRequest> = {
				settings: {
					...params.settings
				}
			}
			return updateOrganizationInformation(params.id, dataOrg)
		},
		onSuccess: async data => {
			if (data?.message === 'Organization updated successfully') {
				refetchOrganizations?.()
			}
		},
		onError: (error: Error) => {
			if (error) {
				showToast('Error', error.message, 'error')
			}
		}
	})

	return (
		<>
			<Modal
				setOpen={setOpen}
				open={open}
				type="normal"
				closeButton
				title="Edit App Build"
				colorIcon="w-[14px]"
				borderIcon="p-[2px]"
			>
				<div className="flex flex-col items-center  mt-5">
					<Typography
						type={TypographyTypes.h2}
						fontLeading="22/27"
						className="!font-normal"
						title={`You can't change app build, since your app has ${cantUsers} users`}
					/>
					<Button
						label="Close"
						className="!min-w-[unset] !py-[9px] w-[224px] mt-5"
						onClick={() => setOpen(false)}
					/>
				</div>
			</Modal>
			<Table
				{...tableProps}
				columns={columns({
					onCopyAAC: handleAACCopy,
					onEditAdminTeam: handleOptionsAdminTeam,
					onEditAppBuild: handleOptionsBuildApp,
					onEditInformation: handleOptionsInformation,
					onView: handleView,
					onCopyMoveOver: handleCopyMoveOver,
					onCopySSO: handleCopySSO,
					onChageStatus,
					onEditVisibility: mutate
				})}
				data={organizations}
				justify="left"
				customWidth={[3, 11, 11, 9, 10, 18, 15, 14, 8, 10, 7]}
				contentLarg
				isFetching={tableProps?.isFetching || isLoading}
				bodyTableClassName="!h-[calc(68vh)] !overflow-y-auto"
				initialStateTable={{
					sortBy: [{ id: 'last_active', desc: true }]
				}}
				setSort={setSort}
			/>
		</>
	)
}
